import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Link } from '@material-ui/core';
import { TemplateScopeType, TemplateType } from '@dev/ee-api/ee-api-models';
import { ArrowDropDownIcon, BasicInput, SimplePopover, Typography, palette } from '@dev/ee-components';

import { ModifyForm, getFormCreatorStore } from '@/store/formCreator/actions';
import {
    getTemplateList,
    StartLoadingTemplateParams,
    StartLoadingTemplates,
} from '@/store/templateList/actions';
import { useSecurity } from '@/services/hooks/useSecurity';
import { debounce } from '@/services/tools/debounce';
import { links } from '@/services/dictionaries/links';

import { useTemplatesList } from '../hooks/useTemplatesList';
import { CustomPopoverBody } from './CustomPopoverBody';
import { DEFAULT_OPTIONS_COUNT } from '..';
import { SimplePopoverWrapper, ArrowDropdownContainer, ActivationEmailTemplateDescWrapper } from './styles';

export const ActivationEmailTemplateDropdown = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { formObject, formPayload } = useSelector(getFormCreatorStore);
    const { isReseller } = useSecurity();
    const inputWidth = document.getElementById("activation-email-dropdown-wrapper")?.offsetWidth || 570;

    const currentTemplate = formPayload?.ActivationTemplateName ?? formObject?.ActivationTemplateName;
    const [chosenOptions, setChosenOptions] = useState<any>(currentTemplate ? {label: currentTemplate, value: currentTemplate} : { label: 'Default', value: 'Default' });
    const [templateSearch, setTemplateSearch] = useState(null);

    const params: StartLoadingTemplateParams = {
        page: 1,
        search: templateSearch,
        limit: DEFAULT_OPTIONS_COUNT,
        type: [TemplateScopeType.Personal],
        templateTypes: [TemplateType.TemplateEditor, TemplateType.RawHTML, TemplateType.DragDropEditor],
        isInfinityScroll: true,
    };

    const { loadingTemplates, hasMore, templatesList } = useSelector(getTemplateList);
    const options = useTemplatesList(templateSearch);

    useEffect((): void => {
        dispatch(ModifyForm({ActivationTemplateName: formObject?.ActivationTemplateName || null}));
    }, []);

    const updateTemplates = (search: string, customOffset?: number) => {
        const offset = customOffset ?? templatesList?.length;

        (hasMore || !offset) && dispatch(StartLoadingTemplates({...params, page:Math.floor(offset/DEFAULT_OPTIONS_COUNT) + 1, search}));
    };

    const handleOnClickDropdown = (e, template): void => {
        if (template.value === "noresults") return;
        setChosenOptions(template);
        setTemplateSearch(null);
        
        dispatch(ModifyForm({ ActivationTemplateName: template.value === 'default' ? null : template.label }));
        templateSearch && updateTemplates(null, 0);
    }

    const handleOnInputChange = (e): void => {
        const value = e?.target?.value || '';
        setChosenOptions({ label: value });
        setTemplateSearch(value);
        debounce((): void => {
            updateTemplates(value, 0);
        });
    };

    return (
        <SimplePopoverWrapper id="activation-email-dropdown-wrapper">
            <SimplePopover
                id='activation-email-template-dropdown'
                type="custom"
                disableSearch
                onItemClick={handleOnClickDropdown}
                //placement="bottom-start"
                maxHeight={450}
                fullWidth
                listItemWidth={inputWidth}
                options={options as any}
                rowsPerChunk={10}
                cachedListSize={10}
                infiniteScrollProps = {{
                    dataLength: options.length || DEFAULT_OPTIONS_COUNT,
                    hasMore: hasMore ?? true,
                    next: () => updateTemplates(templateSearch),
                }}
                // eslint-disable-next-line react/no-unstable-nested-components
                customDropdownBody={(element: any): JSX.Element => (
                    <CustomPopoverBody element={element} />
                )}
                isLoadingOptions={loadingTemplates}
            >
                <BasicInput
                    id="activation-email-template-input"
                    label={t('activationemailtemplate')}
                    value={chosenOptions.label}
                    onChange={handleOnInputChange}
                    endIcon={
                        <ArrowDropdownContainer>
                            <ArrowDropDownIcon color={palette.indigo[6]} />
                        </ArrowDropdownContainer>
                    }
                />
            </SimplePopover>
            <ActivationEmailTemplateDescWrapper>
                <Typography variant="body1" bodyVariant="help">
                    {t('activationemailtemplate_desc')}
                    {!isReseller && <>
                        {' '}
                        {t('activationemailtemplate_link')}{' '}
                            <Link
                                target="_blank"
                                style={{ color: palette.indigo[6] }}
                                href={links.createActivationTemplate}
                            >
                                {t('clickhere')}{'.'}
                            </Link>
                        </>
                    }
                </Typography>
            </ActivationEmailTemplateDescWrapper>
        </SimplePopoverWrapper>
    )
};
